import React, {ReactElement} from "react";
import {Bar, BarChart, CartesianGrid, Legend, PolarAngleAxis, PolarGrid, Radar, RadarChart, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';
import Tribe, {tribeToImageString} from "../../enum/Tribe";
import {useTranslationContext} from "../../../react/providers/TranslationContext";
import getTroopData from "../../data/troops/index";
import TravianVersion from "../../enum/version";
import {getEmptyTroopRow, TribeTroopData, Troop} from "../../data/troops/types";
import {fillPartialFeaturesWithDefaults} from "../../Helper/TravianVersionHandling";
import {TravianVersionWithFeatures} from "../../Version";
import {Building} from "../../enum/Building";
import {getTrainingTime, TrainingParameters} from "../../Helper/TroopCalculators";
import {addResources, getNewResources, multiplyResources, sumResources} from "../../enum/Resource";
import {TravianIcon} from "../../../react/components/ui/tIcon";
import ImageMisc from "../../Components/ImageMisc/ImageMisc";
import ImageUnit from "../../Components/ImageUnit/ImageUnit";
import WithTooltip from "../../../react/components/ui/withTooltip";

type OffCompare = {
    units: Array<Troop>,
    greatBuildings: boolean,
    heroItems: number,
}

type StrengthConfig = {
    upgradeLevel: number,
    allianceMetallurgy: number,
}

type StrengthKey = keyof Pick<Troop, 'attack'|'defenceInfantry'|'defenceCavalry'>;

function getSingleCombatStrength(base: number, supply: number, level: number, metallurgy: number): number {
    return (base + (base + 300 * supply / 7) * (1.007 ** level - 1)) * (1 + metallurgy);
}

function getCombatStrength(unit: Troop, config: StrengthConfig, features: TravianVersionWithFeatures): Record<StrengthKey, number> {
    const metallurgy = (config.allianceMetallurgy && features.version === TravianVersion.Legends) ? config.allianceMetallurgy : 0;

    return {
        attack: getSingleCombatStrength(unit.attack, unit.supply, config.upgradeLevel, metallurgy),
        defenceInfantry: getSingleCombatStrength(unit.defenceInfantry, unit.supply, config.upgradeLevel, metallurgy),
        defenceCavalry: getSingleCombatStrength(unit.defenceCavalry, unit.supply, config.upgradeLevel, metallurgy),
    }
}

function getTrainingBuilding(unit: Troop): Building {
    if ([7, 8].includes(unit.id)) {
        return Building.Workshop;
    }
    if ([9, 10].includes(unit.id)) {
        return Building.Residence;
    }
    return unit.isInfantry ? Building.Barracks : Building.Stable;
}

export default function OffComparison(props: object): ReactElement {
    const {getTranslator} = useTranslationContext();
    const translate = getTranslator('react/component/Travian/TribeComparison');

    const features = fillPartialFeaturesWithDefaults({ version: TravianVersion.Legends, features: {} });

    const troopData = getTroopData(features);

    const comparisonSelection = [
        [ troopData[Tribe.Roman]['3'], troopData[Tribe.Roman]['5'], troopData[Tribe.Roman]['7'], ],
        [ troopData[Tribe.Roman]['3'], troopData[Tribe.Roman]['6'], troopData[Tribe.Roman]['7'], ],
        [ troopData[Tribe.Teuton]['1'], troopData[Tribe.Teuton]['6'], troopData[Tribe.Teuton]['7'], ],
        [ troopData[Tribe.Teuton]['3'], troopData[Tribe.Teuton]['6'], troopData[Tribe.Teuton]['7'], ],
        [ troopData[Tribe.Gaul]['2'], troopData[Tribe.Gaul]['4'], troopData[Tribe.Gaul]['7'], ],
        [ troopData[Tribe.Gaul]['2'], troopData[Tribe.Gaul]['6'], troopData[Tribe.Gaul]['7'], ],
        [ troopData[Tribe.Egypt]['3'], troopData[Tribe.Egypt]['6'], troopData[Tribe.Egypt]['7'], ],
        [ troopData[Tribe.Hun]['1'], troopData[Tribe.Hun]['4'], troopData[Tribe.Hun]['7'], ],
        [ troopData[Tribe.Hun]['1'], troopData[Tribe.Hun]['5'], troopData[Tribe.Hun]['7'], ],
        [ troopData[Tribe.Hun]['1'], troopData[Tribe.Hun]['6'], troopData[Tribe.Hun]['7'], ],
        [ troopData[Tribe.Hun]['2'], troopData[Tribe.Hun]['4'], troopData[Tribe.Hun]['7'], ],
        [ troopData[Tribe.Hun]['2'], troopData[Tribe.Hun]['5'], troopData[Tribe.Hun]['7'], ],
        [ troopData[Tribe.Hun]['2'], troopData[Tribe.Hun]['6'], troopData[Tribe.Hun]['7'], ],
        [ troopData[Tribe.Spartan]['1'], troopData[Tribe.Spartan]['6'], troopData[Tribe.Spartan]['7'], ],
        [ troopData[Tribe.Spartan]['4'], troopData[Tribe.Spartan]['6'], troopData[Tribe.Spartan]['7'], ],
        [ troopData[Tribe.Viking]['1'], troopData[Tribe.Viking]['6'], troopData[Tribe.Viking]['7'], ],
        [ troopData[Tribe.Viking]['3'], troopData[Tribe.Viking]['6'], troopData[Tribe.Viking]['7'], ],
    ]

    const compare: Array<OffCompare> = comparisonSelection.map(units => ({ units, greatBuildings: false, heroItems: 0.15 }));

    const trainingConfig: Partial<TrainingParameters> = {
        buildingLevel: 20,
        heroBonus: 0.15,
        legendsRecruitment: 0.1,
        // Calculator checks tribe for us.
        horseDrinkingTrough: 20,
    }

    const strengthConfig: StrengthConfig = {
        upgradeLevel: 20,
        allianceMetallurgy: 0.1,
    }

    const results = compare.map(config => {
        // Calculated stats after 24 hours.
        const cumulated = {
            attackTotal: 0,
            attackInfantry: 0,
            attackCavalry: 0,
            supply: 0,
            troops: getEmptyTroopRow(),
            cost: getNewResources(),
        };

        const heroWeaponBonus: { attack: number, unit: null|Troop } = {
            attack: 0,
            unit: null,
        };

        config.units.forEach(unit => {
            const myTrainingBuilding = getTrainingBuilding(unit);
            const factor = config.units.filter(_unit => getTrainingBuilding(_unit) === myTrainingBuilding).length;
            const trainingTime = getTrainingTime(unit, trainingConfig, features) * factor;
            const strength = getCombatStrength(unit, strengthConfig, features);
            const greatBuildings = config.greatBuildings && myTrainingBuilding !== Building.Workshop;

            // HDT bonus
            const unitSupply = unit.supply - (!unit.isInfantry && unit.tribeId === Tribe.Roman ? 1 : 0);

            const amount = 86400 / trainingTime * (greatBuildings ? 2 : 1);
            cumulated.troops[unit.id] += amount;
            cumulated.supply += unitSupply * amount;
            cumulated[unit.isInfantry ? 'attackInfantry' : 'attackCavalry'] += strength.attack * amount;
            cumulated.cost = addResources(cumulated.cost, multiplyResources(getNewResources(...unit.trainingCost), amount * (greatBuildings ? 2 : 1)));

            if (myTrainingBuilding !== Building.Workshop) {
                const weaponBonus = amount * 5 * unit.supply * (unit.tribeId === Tribe.Spartan ? 1.3333 : 1.0);
                if (heroWeaponBonus.attack < weaponBonus) {
                    heroWeaponBonus.attack = weaponBonus;
                    heroWeaponBonus.unit = unit;
                }
            }
        });

        if (heroWeaponBonus.unit !== null) {
            cumulated[heroWeaponBonus.unit.isInfantry ? 'attackInfantry' : 'attackCavalry'] += heroWeaponBonus.attack;
        }

        cumulated.attackTotal = cumulated.attackCavalry + cumulated.attackInfantry;

        const totalRaidingUnits = config.units
            .filter(unit => unit.carry > 0)
            .map(unit => cumulated.troops[unit.id])
            .reduce((a, b) => a + b, 0);

        // Total raiding score - raiding with all units which can carry stuff
        const totalRaidingScore = config.units
            .map(unit => unit.speed * unit.carry * (cumulated.troops[unit.id] / totalRaidingUnits))
            .reduce((a, b) => a + b, 0);

        // Peak raiding score - only raid with the unit best suited for it
        const peakRaidingScore = config.units.map(unit => unit.speed * unit.carry).sort((a, b) => b - a)[0];

        const stats = {
            attackPerDay: cumulated.attackTotal,
            costPerHour: sumResources(multiplyResources(cumulated.cost, 1/24)),
            hoursPerCost: 1/sumResources(multiplyResources(cumulated.cost, 1/24)),
            supplyPerAttack: cumulated.supply / cumulated.attackTotal,
            attackPerSupply: cumulated.attackTotal / cumulated.supply,
            costPerAttack: cumulated.attackTotal / sumResources(cumulated.cost),
            attackPerCost: sumResources(cumulated.cost) / cumulated.attackTotal,
            totalRaidingScore,
            peakRaidingScore,
        };

        return {
            config,
            cumulated,
            stats,
            normalizedStats: null,
        }
    });

    // Normalize all stats to [0, 1] range for radial charts

    const peakStats = {
        attackPerDay: 0,
        costPerHour: 0,
        hoursPerCost: 0,
        supplyPerAttack: 0,
        attackPerSupply: 0,
        costPerAttack: 0,
        attackPerCost: 0,
        totalRaidingScore: 0,
        peakRaidingScore: 0,
    };

    results.forEach(result => {
        const {stats} = result;
        for (const key in stats) {
            peakStats[key] = Math.max(stats[key], peakStats[key]);
        }
    })

    results.forEach(result => {
        const normalizedStats = {...result.stats};
        for (const key in result.stats) {
            normalizedStats[key] = result.stats[key] / peakStats[key]
        }
        result.normalizedStats = normalizedStats;
    })


    const data = [
        {
            category: translate('offAttackPerTime'),
        },
        {
            category: translate('offCostPerHour'),
        },
        {
            category: translate('offSupplyPerAttack'),
        },
        {
            category: translate('offPeakRaidingScore'),
        },
    ];

    const directCompare = [2, 7];
    const colors = [
        '#ff0000',
        '#ffff00',
        '#0000ff',
        '#00ff00',
    ]
    for (const index of directCompare) {
        const {normalizedStats} = results[index];
        data[0]['result' + index] = normalizedStats.attackPerDay;
        data[1]['result' + index] = normalizedStats.hoursPerCost;
        data[2]['result' + index] = normalizedStats.attackPerSupply;
        data[3]['result' + index] = normalizedStats.peakRaidingScore;
    }

    const barData = results.map(result => {
        return {
            name: result.config.units[0].tribeId + '-' + result.config.units.map(unit => unit.id).join(''),
            ...result.stats,
        };
    })

    const selection = 'attackPerDay';

    barData.sort((a, b) => b[selection] - a[selection]);

    return <>
        <div style={{height: '400px', width: '1000px'}}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData}>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='name' height={64} width={48} tick={<CustomXAxis />} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey={selection} fill='#ff0000' />
                </BarChart>
            </ResponsiveContainer>
        </div>
        <div style={{height: '400px', width: '600px'}}>
            <ResponsiveContainer width="100%" height="100%">
                <RadarChart cx="50%" cy="50%" outerRadius="85%" data={data}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="category" />
                    {directCompare.map((resultIndex, compareIndex) =>
                        <Radar dataKey={'result' + resultIndex} key={resultIndex} stroke={colors[compareIndex]} fill={colors[compareIndex]} fillOpacity={0.4} />
                    )}
                </RadarChart>
            </ResponsiveContainer>
        </div>
    </>
}

function CustomXAxis(props) {
    const {translate} = useTranslationContext();
    const tribe = Number(props.payload.value.split('-')[0]) as Tribe;
    const units: Array<keyof TribeTroopData> = props.payload.value.split('-')[1].split('').map(n => Number(n));

    return <foreignObject x={props.x - 24} y={props.y} width={48} height={64}>
        <div className='text-center'>
            <WithTooltip tooltip={translate('travian/tribe' + tribe.toString())}><ImageMisc type={tribeToImageString(tribe)} size={32} /></WithTooltip><br />
            {units.map((unit, index) => <ImageUnit key={index} tribe={tribe} troopId={unit} size={16} />)}
        </div>
    </foreignObject>
}
